import React, { useEffect } from 'react';
import { motion, AnimatePresence, useDragControls } from 'motion/react';

export interface SwipeModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  height?: string;
  paddingBottom?: string;
  className?: string;
  overlayHandle?: boolean;
}

const SwipeModal: React.FC<SwipeModalProps> = ({
  isOpen,
  onClose,
  children,
  height,
  paddingBottom,
  className = '',
  overlayHandle = false,
}) => {
  const dragControls = useDragControls();

  // Prevent body scrolling when the modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div 
            className="fixed inset-0 bg-black/60 z-[55] pointer-events-auto"
            style={{ backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Drawer */}
          <motion.div 
            className={`fixed bottom-0 left-0 right-0 z-[60] bg-[#0a0a0a] rounded-t-[32px] border-t border-white/10 shadow-[0_-10px_40px_rgba(0,0,0,0.5)] focus:outline-none pointer-events-auto ${className}`}
            style={{ 
              height: height || '90vh',
              paddingBottom: paddingBottom,
            }}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%", transition: { duration: 0.25, ease: 'easeIn' } }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            drag="y"
            dragControls={dragControls}
            dragListener={false}
            dragConstraints={{ top: 0, bottom: 300 }}
            dragElastic={0.3}
            onDragEnd={(_, info) => {
              if (info.offset.y > 80 || info.velocity.y > 400) {
                onClose();
              }
            }}
          >
            <div className="w-full h-full flex flex-col overflow-hidden relative rounded-t-[32px]">
              {/* Draggable Handle Area */}
              <div 
                className="absolute top-0 left-1/2 -translate-x-1/2 w-[60%] h-12 flex justify-center pt-3 shrink-0 z-50 touch-none cursor-grab active:cursor-grabbing drop-shadow-md"
                onPointerDown={(e) => dragControls.start(e)}
              >
                <div className="w-12 h-1.5 bg-gray-400 rounded-full" />
              </div>
              
              {/* Content taking full rest space */}
              <div 
                className={`w-full h-full flex flex-col overflow-hidden ${!overlayHandle ? 'pt-8' : ''}`}
              >
                {children}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SwipeModal;
